import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { getSupabase } from "@/lib/supabase-lazy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/reset-password")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Set a new password — The Founders App" },
      {
        name: "description",
        content: "Choose a new password for your The Founders App account after a reset request.",
      },
      { property: "og:title", content: "Set a new password — The Founders App" },
      { property: "og:description", content: "Finish resetting your The Founders App password." },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    void (async () => {
      const supabase = await getSupabase();
      const { data } = await supabase.auth.getSession();
      if (!cancelled) setReady(Boolean(data.session));
      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session) setReady(true);
      });
      if (cancelled) sub.subscription.unsubscribe();
      else unsubscribe = () => sub.subscription.unsubscribe();
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Those passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await (await getSupabase()).auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      void navigate({ to: "/app/today" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update your password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main" className="flex min-h-screen items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm">
        <Link to="/" className="text-xs tracking-[0.3em] text-muted-foreground uppercase">
          The Founders App
        </Link>
        <h1 className="mt-6 font-display text-3xl">Set a new password</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {ready
            ? "Choose something only you would pick. At least 8 characters."
            : "Open this page from the link in your reset email to continue."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              disabled={!ready}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              disabled={!ready}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy || !ready}>
            Update password
          </Button>
        </form>

        <Link
          to="/auth"
          className="mt-6 block text-center text-sm text-muted-foreground hover:text-foreground"
        >
          Back to sign in
        </Link>
      </div>
    </main>
  );
}
